import videojs from 'video.js';
import type Player from 'video.js/dist/types/player';

import { Timer } from '~/utils/timer';
import {
  RemoteEventType,
  toNumberWithDefault,
} from '~/services/continue-watching/client.ts';
import { playerID } from '~/routes/player';

let isStarted: boolean = false;
let isPaused: boolean = false;
let currentIntervalSegment: number = 0;
let totalSession: number = 0;
let playbackPosition: number | undefined = 0;
let timer: Timer | null = null;
let previousUpdateTime: number = 0;
let isAd: boolean = false;
let isSeeking: boolean = false;

export function videoIntervalTracker(
  player: Player,
  video_interval: number,
  sendEvent: (a: RemoteEventType, b: any) => void
): any {
  const videoInterval = toNumberWithDefault(30, video_interval);

  function onPlay() {
    if (isPaused) {
      isPaused = false;
    }

    if (!isStarted) {
      isStarted = true;
    }
  }

  function onPause(this: any) {
    if (!isPaused) {
      isPaused = true;
    }
  }

  function onClose(this: any) {
    removeListeners();
    stopTracking();
    isStarted = false;
    currentIntervalSegment = 0;
    totalSession = 0;
    playbackPosition = 0;
    previousUpdateTime = 0;
  }

  function onAdStart(this: any) {
    isAd = true;
  }

  function onAdEnd(this: any) {
    isAd = false;
  }

  function onSeeking(this: any) {
    isSeeking = true;
  }

  function onSeekEnd(this: any) {
    isSeeking = false;
  }

  function onTimerFired() {
    const player = videojs(playerID);

    if (!player) {
      return;
    }

    const buffered = player.bufferedEnd();

    if (!isStarted) {
      // Wait until playback begins
      startTracking();

      return;
    }

    if (Number(player?.currentTime()) > 0) {
      playbackPosition = player?.currentTime();
    }

    const currentTimeNow = Date.now();

    if (
      isPaused &&
      currentIntervalSegment > 0 &&
      Number(player?.currentTime()) > 0
    ) {
      // Send interval data at the moment when playback is paused
      onVideoIntervalEvent(
        currentIntervalSegment,
        totalSession,
        playbackPosition
      );

      currentIntervalSegment = 0;
    }

    if (!isPaused && !isAd && !isSeeking && buffered > 0) {
      if (!previousUpdateTime) {
        currentIntervalSegment += 1;
        totalSession += 1;
      } else {
        const tickTime = (currentTimeNow - previousUpdateTime) / 1000;
        currentIntervalSegment += tickTime;
        totalSession += tickTime;
      }

      while (currentIntervalSegment >= videoInterval) {
        currentIntervalSegment -= videoInterval;

        onVideoIntervalEvent(videoInterval, totalSession, playbackPosition);
      }
    } else {
      // Skip interval segment if players state is not satisfied
    }

    previousUpdateTime = currentTimeNow;
    startTracking();
  }

  function startTracking() {
    const oneSecondTimer = 1000;

    timer?.clear();
    timer = new Timer(onTimerFired, oneSecondTimer);
    timer.start();
  }

  function stopTracking() {
    timer?.clear();
    timer = null;

    // Send interval data at the moment when playback is stopped
    onVideoIntervalEvent(
      currentIntervalSegment,
      totalSession,
      playbackPosition
    );
  }

  function onVideoIntervalEvent(
    interval: any,
    totalSession: any,
    playbackPosition: any
  ) {
    sendEvent(RemoteEventType.VideoInterval, {
      totalSession,
      playbackPosition,
      interval,
    });
  }

  const removeListeners = () => {
    player.off('dispose', onClose);
    player.off('pause', onPause);
    player.off('play', onPlay);
    player.off('adstart', onAdStart);
    player.off('adend', onAdEnd);
    player.off('seeking', onSeeking);
    player.off('seeked', onSeekEnd);
  };

  const addListeners = () => {
    player.on('dispose', onClose);
    player.on('pause', onPause);
    player.on('play', onPlay);
    player.on('adstart', onAdStart);
    player.on('adend', onAdEnd);
    player.on('seeking', onSeeking);
    player.on('seeked', onSeekEnd);
  };

  addListeners();
  startTracking();
}
